import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatPhone,
  formatPlate,
  formatQuantity,
  t,
} from "@hakili/i18n";
import { GuardedButton } from "@hakili/ui";
import { AlertTriangle, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

import { CopyableLink } from "./copyable-link.js";

import { type GridFamily, type ReportEntry, ReportForm } from "./dossier.js";

/**
 * `round CN-A`, `CN-A.3`: **« Expertises », to the drawing of 10 September**
 * (`hakili-console.jsx`, `ExpertisesScreen`), `WF-CON-30` to `34` as the
 * founder amended them (`ADDENDUM-2026-09-10-console.md` §3.1 to §3.4).
 *
 * The queue with Hakili's proposal per pending demand , the zone's cabinet, the
 * first OPEN day with room, the motive in words , and the two selectors
 * prefilled with it, modifiable until the confirmation; the days with their
 * ARREST said on the screen and never merely applied; each day's composition
 * cabinet by cabinet, stop by stop; the tour sheet with its version and its
 * list, sent by the door and its link shown ONCE to the office; the
 * acknowledgement naming the version it covers; the signal in its three
 * shapes; the report entry per programmed passage; and the séquestre , the
 * verification fees with their state, and « Confirmer la réception du
 * paiement » on any pending payment (every means is confirmed by staff while no
 * mobile-money provider is connected), calling `C3-A`'s door.
 *
 * ## What the drawing shows that this screen does not, and says
 *
 * - « Réaffecter à <cabinet> » on a stop (`WF-CON-31`): no door moves a whole
 *   stop today; the control keeps the drawing's look, greyed, with its round.
 * - the report as a DOCUMENT (§3.4): `C6`'s; the entry form stands, the
 *   document is named and greyed.
 * - « Taux de conformité »: the intelligence round's figure, a dash.
 * - the cabinets' directory and onboarding: the drawing places them in
 *   « Partenaires », and there they are.
 * - the séquestre has no drawing; it is built IN WORDS, its pair pending the
 *   designer's stroke, as the order had it.
 */

export interface DayCabinetLoad {
  readonly cabinetId: string;
  readonly cabinetName: string;
  readonly capacity: number;
  readonly placed: number;
  /** `round C5-2`, `M4`, `OD-30`: the cabinet's own declaration for the day; `null` when the minimum stands. */
  readonly declared?: number | null;
  readonly minimum?: number;
  readonly belowMinimum?: boolean;
  readonly overflow?: number;
}

export interface DayView {
  readonly id: string;
  readonly label: string;
  readonly dayOn: string;
  readonly arrested: boolean;
  readonly arrestAt: string;
  readonly inDays: number;
  readonly cabinets: readonly DayCabinetLoad[];
}

/** The drawing's `RAISONS_EMPECHEMENT` (`hakili-expert.jsx`), in the queue's words. */
const PREVENTED_WORDS: ReadonlyMap<string, string> = new Map([
  ["absent", "console.expertises.empecheAbsent"],
  ["cles", "console.expertises.empecheCles"],
  ["batterie", "console.expertises.empecheBatterie"],
  ["inaccessible", "console.expertises.empecheInaccessible"],
  ["ferme", "console.expertises.empecheFerme"],
  ["autre", "console.expertises.empecheAutre"],
]);
const preventedWords = (reason: string, detail: string | null): string => {
  const key = PREVENTED_WORDS.get(reason);
  const words = key === undefined ? reason : t(key);
  return detail === null ? words : `${words} : ${detail}`;
};

export interface QueueLine {
  readonly demandId: string;
  readonly reference: string;
  readonly sellerName: string;
  readonly clientName: string | null;
  readonly vehicleLabel: string;
  readonly feesPaidOn: string | null;
  /** `round C5-2`, decision D: the pass failed; the cabinet's reason, back on the queue. */
  readonly preventedReason?: string | null;
  readonly preventedDetail?: string | null;
  readonly proposal: {
    readonly cabinetId: string | null;
    readonly cabinetName: string | null;
    readonly expertiseDayId: string | null;
    readonly dayLabel: string | null;
    readonly rule: string;
    readonly motive: string;
  };
  /**
   * `U-3c`, `M2` (`P3-ITL-005`): the row this proposal was RECORDED as
   * (`intelligence.scheduling_proposals`) , the sentence below the selectors is that row's own
   * reason and not a second computation. `null` where no cabinet is active and nothing was stored:
   * the screen then says so in words (`R81`).
   */
  readonly proposalId?: string | null;
}

export interface SheetView {
  readonly id: string;
  readonly version: number;
  readonly sentAt: string;
  readonly sentChannel: string;
  readonly demandReferences: readonly string[];
  readonly acknowledgedAt: string | null;
  readonly acknowledgedVersion: number | null;
  readonly sentDaysAgo: number;
  readonly signal: "sansAccuse" | "divergente" | "accuseAncien" | null;
  readonly diff: { readonly added: readonly string[]; readonly removed: readonly string[] };
  /**
   * `round C5.1`, `C5.1.0`: the facts as they stood when THIS version left ,
   * the same rows the cabinet's page reads. `[]` on a version sent before.
   */
  readonly stops: readonly SheetStopView[];
}

export interface SheetStopView {
  readonly sellerOrganisationId: string;
  readonly sellerName: string;
  readonly address: string | null;
  readonly contactName: string | null;
  readonly contactPhone: string | null;
  readonly vehicles: readonly {
    readonly demandId: string;
    readonly reference: string;
    readonly stockReference: string;
    readonly vehicleLabel: string;
    readonly vehiclePlate: string | null;
    readonly mileageKm: number;
    readonly colour: string;
    readonly clientName: string | null;
  }[];
}

export interface StopVehicle {
  readonly demandId: string;
  readonly reference: string;
  readonly vehicleLabel: string;
  readonly vehiclePlate: string | null;
  readonly clientName: string | null;
  readonly isStockControl: boolean;
  readonly scheduledAfterArrest: boolean;
  /** `T-2b`, `M1 (d)`: the dealer's mise à disposition (WF-DLR-07 step 3), read from the demand. */
  readonly madeAvailableAt?: string | null;
}

export interface CabinetDayView extends DayCabinetLoad {
  readonly stops: readonly {
    sellerOrganisationId: string;
    sellerName: string;
    vehicles: readonly StopVehicle[];
  }[];
  readonly liveReferences: readonly string[];
  readonly sheet: SheetView | null;
}

export interface DemandView {
  readonly id: string;
  readonly reference: string;
  readonly vehicleLabel: string;
  readonly sellerName: string;
  readonly clientName: string | null;
  readonly status: string;
  readonly displayStatus: string;
  readonly cabinetId: string | null;
  readonly expertiseDayId: string | null;
  readonly scheduledAt: string | null;
}

export interface EscrowFeeView {
  readonly id: string;
  readonly dossierReference: string | null;
  readonly clientName: string | null;
  readonly vehicleLabel: string | null;
  readonly clientAmount: number;
  readonly method: string | null;
  readonly requestReference: string | null;
  readonly requestStatus: string | null;
  readonly state: string;
  readonly settledAt: string | null;
  readonly createdAt: string;
  /** `CN-B.0(b)`: when the transfer was asked; the bank's reference and the date it landed once confirmed. */
  readonly requestedAt: string | null;
  readonly bankReference: string | null;
  readonly receivedOn: string | null;
  /** #493 addendum 2: when the client said « j'ai effectué le paiement » and the reference of his receipt , a signal, not the receipt. */
  readonly declaredAt?: string | null;
  readonly clientReference?: string | null;
  /** `PAY-P`: the whole price and the cabinet's fee due, `null` until placed and entered. */
  readonly totalAmount?: number;
  readonly cabinetFeeAmount?: number | null;
}

export interface ArrestView {
  readonly hours: number;
  readonly label: string;
}

export interface ExpertisesProps {
  readonly vue: string;
  readonly queue: readonly QueueLine[];
  readonly arrest: ArrestView;
  readonly days: readonly DayView[];
  readonly dayDetails: Readonly<Record<string, readonly CabinetDayView[]>>;
  readonly demands: readonly DemandView[];
  readonly grid: readonly GridFamily[] | null;
  readonly fees: readonly EscrowFeeView[];
  readonly links: Readonly<Record<string, string>>;
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly onOpenDay: (dayId: string) => void;
  readonly onSchedule: (
    demandId: string,
    cabinetId: string,
    dayId: string,
    afterArrestOverride: boolean,
  ) => void;
  readonly onSendSheet: (dayId: string, cabinetId: string) => void;
  readonly onFileReport: (demandId: string, entry: ReportEntry) => void;
  /** `CN-B.0(a)`: answers whether the door accepted, so the form can close on its own answer. */
  readonly onConfirmTransfer: (
    feeId: string,
    amount: number,
    bankReference: string,
    receivedOn: string,
  ) => Promise<boolean>;
  /** `CN-B.0(d)`, WF-CON-31: the whole stop to another cabinet. */
  readonly onReassignStop: (
    dayId: string,
    fromCabinetId: string,
    sellerOrganisationId: string,
    toCabinetId: string,
  ) => Promise<boolean>;
}

const DASH = t("common.valeur.tiret");

const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, value), t(key));
const count = (n: number, one: Parameters<typeof t>[0], many: Parameters<typeof t>[0]): string =>
  n === 1 ? t(one) : `${String(n)} ${t(many)}`;
const daysWords = (n: number): string => count(n, "console.expertises.jourUn", "console.expertises.jours");

export function Expertises(props: ExpertisesProps): React.JSX.Element {
  const pendingEscrow = props.fees
    .filter((fee) => fee.state === "recu")
    .reduce((sum, fee) => sum + fee.clientAmount, 0);
  const capacityPerDay = props.days[0]?.cabinets.reduce((sum, one) => sum + one.capacity, 0) ?? 0;
  const programmed = props.days.reduce(
    (sum, day) => sum + day.cabinets.reduce((inner, one) => inner + one.placed, 0),
    0,
  );
  // The drawing's own opening: the pending demands grouped by seller, the
  // busiest seller first , what « regrouper » means in numbers.
  const bySeller = new Map<string, QueueLine[]>();
  for (const line of props.queue)
    bySeller.set(line.sellerName, [...(bySeller.get(line.sellerName) ?? []), line]);
  const grouping = [...bySeller.entries()]
    .sort((a, b) => b[1].length - a[1].length)
    .map(
      ([seller, lines]) =>
        `${count(lines.length, "console.expertises.chezUn", "console.expertises.chez")} ${seller} (${lines
          .map((line) => `${line.reference} ${line.vehicleLabel}`)
          .join(", ")})`,
    )
    .join(", ");
  return (
    <main className="flex-1 overflow-y-auto">
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">{t("console.expertises.titre")}</h1>
          <p className="mt-1.5 text-sm text-gray-500">{t("console.expertises.sousTitre")}</p>
        </div>

        <div className="mb-6 grid grid-cols-4 gap-4">
          <Stat
            label={t("console.expertises.statAProgrammer")}
            value={String(props.queue.length)}
            sub={t("console.expertises.statAProgrammerSous")}
          />
          <Stat
            label={t("console.expertises.statProgrammees")}
            value={String(programmed)}
            sub={t("console.expertises.statProgrammeesSous")}
          />
          <Stat
            label={t("console.expertises.statSequestre")}
            value={formatMoney(pendingEscrow)}
            sub={t("console.expertises.statSequestreSous")}
          />
          <Stat
            label={t("console.expertises.statConformite")}
            value={DASH}
            sub={t("console.expertises.statConformiteSous")}
          />
        </div>

        <Signals {...props} />

        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
          <div>
            <p className="text-sm font-medium text-gray-900">{t("console.expertises.regrouperTitre")}</p>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              {grouping === "" ? "" : `${grouping}. `}
              {t("console.expertises.regrouperTexte")}
            </p>
          </div>
        </div>

        <section data-testid="expertises-jours" className="mb-6">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">{t("console.expertises.joursTitre")}</h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {t("console.expertises.joursCapacite")} · {String(capacityPerDay)}
              </p>
            </div>
            {/* `WF-CON-30` step 8, the drawing's « Tout confirmer »: every
                proposal as it stands, each through the same door. */}
            {props.queue.length === 0 ? null : (
              <button
                type="button"
                data-testid="tout-confirmer"
                disabled={props.busy}
                onClick={() => {
                  for (const line of props.queue) {
                    if (line.proposal.cabinetId === null || line.proposal.expertiseDayId === null) continue;
                    props.onSchedule(
                      line.demandId,
                      line.proposal.cabinetId,
                      line.proposal.expertiseDayId,
                      false,
                    );
                  }
                }}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                {t("console.expertises.toutConfirmer")}
              </button>
            )}
          </div>
          <div className="mb-3 rounded-2xl border border-gray-200 bg-white px-5 py-4">
            <p className="text-sm font-semibold">{t("console.expertises.commentTitre")}</p>
            <ol className="mt-2 space-y-1.5 text-sm text-gray-600">
              {(
                [
                  t("console.expertises.regles1"),
                  t("console.expertises.regles2"),
                  t("console.expertises.regles3"),
                  fill("console.expertises.regles4", {
                    label: props.arrest.label,
                    hours: String(props.arrest.hours),
                  }),
                ] as const
              ).map((rule, index) => (
                <li key={rule} className="flex items-start gap-2">
                  <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-[10px] font-medium text-indigo-700">
                    {String(index + 1)}
                  </span>
                  <span>{rule}</span>
                </li>
              ))}
            </ol>
          </div>
          {/* The drawing shows three Thursdays; the calendar holds many more
              (`P1-EXP-024` keeps it full). The next three are the horizon a
              team plans on, in the drawing's own three columns; the queue's
              selector still offers every day. An opened day unfolds below. */}
          <ul className="grid grid-cols-3 gap-3">
            {props.days.slice(0, 3).map((day) => (
              <DayCard key={day.id} day={day} {...props} />
            ))}
          </ul>
          {props.days.slice(0, 3).map((day) => {
            const detail = props.dayDetails[day.id];
            return detail === undefined ? null : (
              <div
                key={day.id}
                data-testid={`jour-ouvert-${day.id}`}
                className="mt-3 rounded-2xl border border-gray-200 bg-white px-5 py-4"
              >
                <p className="mb-3 text-sm font-medium text-gray-900">{day.label}</p>
                <div className="space-y-3">
                  {detail.map((cabinet) => (
                    <CabinetBlock
                      key={cabinet.cabinetId}
                      day={day}
                      cabinet={cabinet}
                      siblings={detail}
                      {...props}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </section>

        <Queue {...props} />
        <Reports {...props} />
        <Escrow {...props} />

        <div className="mt-6 rounded-2xl border border-gray-200 bg-white px-5 py-4 text-sm leading-relaxed text-gray-600">
          <p>{t("console.expertises.pied")}</p>
          <p className="mt-2 text-sm text-gray-500">{t("console.expertises.piedCadence")}</p>
          <p className="mt-2 text-sm text-gray-500">{t("console.expertises.piedAnnuaire")}</p>
        </div>
      </div>
    </main>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }): React.JSX.Element {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      <p className="mt-1 text-sm text-gray-500">{sub}</p>
    </div>
  );
}

// ------------------------------------------------------------------ signals

/** `WF-CON-33`'s three shapes, each in the drawing's own sentence, above the flow. */
function Signals(props: ExpertisesProps): React.JSX.Element | null {
  const lines: { key: string; text: string }[] = [];
  for (const day of props.days) {
    for (const cabinet of props.dayDetails[day.id] ?? []) {
      const sheet = cabinet.sheet;
      if (!sheet?.signal) continue;
      const vehicles = count(
        cabinet.liveReferences.length,
        "console.expertises.vehiculeUn",
        "console.expertises.vehicules",
      );
      if (sheet.signal === "divergente") {
        const parts: string[] = [];
        if (sheet.diff.added.length > 0)
          parts.push(
            `${count(sheet.diff.added.length, "console.expertises.ajouteUn", "console.expertises.ajoutes")} (${sheet.diff.added.join(", ")})`,
          );
        if (sheet.diff.removed.length > 0)
          parts.push(
            `${count(sheet.diff.removed.length, "console.expertises.retireUn", "console.expertises.retires")} (${sheet.diff.removed.join(", ")})`,
          );
        lines.push({
          key: `${day.id}-${cabinet.cabinetId}`,
          text: fill("console.expertises.signalDivergente", {
            cabinet: cabinet.cabinetName,
            version: String(sheet.version),
            gap: parts.join(", "),
            sentence:
              sheet.acknowledgedVersion === null
                ? t("console.expertises.signalDivergenteNonAccusee")
                : t("console.expertises.signalDivergenteAccusee"),
            next: String(sheet.version + 1),
          }),
        });
      } else if (sheet.signal === "accuseAncien") {
        lines.push({
          key: `${day.id}-${cabinet.cabinetId}`,
          text: fill("console.expertises.signalAccuseAncien", {
            cabinet: cabinet.cabinetName,
            acknowledged: String(sheet.acknowledgedVersion ?? DASH),
            day: day.label,
            version: String(sheet.version),
          }),
        });
      } else {
        const sinceDays = sheet.sentDaysAgo;
        const inDays = day.inDays;
        lines.push({
          key: `${day.id}-${cabinet.cabinetId}`,
          text: fill("console.expertises.signalSansAccuse", {
            since:
              sinceDays === 0
                ? t("console.expertises.aujourdhui")
                : `${t("console.expertises.ilYA")} ${daysWords(sinceDays)}`,
            cabinet: cabinet.cabinetName,
            day: day.label,
            when:
              inDays === 0
                ? t("console.expertises.aujourdhui")
                : `${t("console.expertises.dans")} ${daysWords(inDays)}`,
            count: vehicles,
          }),
        });
      }
    }
  }
  if (lines.length === 0) return null;
  return (
    <div data-testid="expertises-signaux" className="mb-6 space-y-2">
      {lines.map((line) => (
        <div
          key={line.key}
          className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-3.5"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" />
          <p className="text-sm leading-relaxed text-rose-700">{line.text}</p>
        </div>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------- days

function DayCard({ day, ...props }: ExpertisesProps & { readonly day: DayView }): React.JSX.Element {
  const capacity = day.cabinets.reduce((sum, one) => sum + one.capacity, 0);
  const placed = day.cabinets.reduce((sum, one) => sum + one.placed, 0);
  const remaining = capacity - placed;
  const detail = props.dayDetails[day.id];
  const hasConfirmed = placed > 0;
  return (
    <li data-testid={`jour-${day.id}`} className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-gray-900">{day.label}</p>
          {/* The ARREST, said on the screen: a rule the console applies without
              showing it is a rule the team discovers by suffering it. */}
          <p
            data-testid="jour-arret"
            className={"mt-0.5 text-sm " + (day.arrested ? "text-rose-700" : "text-gray-500")}
          >
            {day.arrested
              ? fill("console.expertises.arretee", {
                  label: props.arrest.label,
                  hours: String(props.arrest.hours),
                })
              : fill("console.expertises.ouverte", {
                  label: props.arrest.label,
                  hours: String(props.arrest.hours),
                })}
          </p>
        </div>
        <span
          className={
            "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " +
            (remaining <= 0 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700")
          }
        >
          {remaining <= 0
            ? t("console.expertises.complet")
            : remaining === 1
              ? t("console.expertises.placeRestanteUne")
              : `${String(remaining)} ${t("console.expertises.placesRestantes")}`}
        </span>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full bg-indigo-600"
          style={{ width: `${String(capacity === 0 ? 0 : Math.min(100, (placed / capacity) * 100))}%` }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {fill("console.expertises.programmees", { placed: String(placed), capacity: String(capacity) })}
        </p>
        <button
          type="button"
          data-testid="jour-detail"
          onClick={() => {
            props.onOpenDay(day.id);
          }}
          className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
        >
          {detail === undefined ? t("console.expertises.voirQuiVaOu") : t("console.expertises.masquerDetail")}
        </button>
      </div>
      {hasConfirmed ? null : (
        <p className="mt-2 text-sm text-gray-500">{t("console.expertises.rienNePart")}</p>
      )}
    </li>
  );
}

function CabinetBlock({
  day,
  cabinet,
  siblings,
  ...props
}: ExpertisesProps & {
  readonly day: DayView;
  readonly cabinet: CabinetDayView;
  readonly siblings: readonly CabinetDayView[];
}): React.JSX.Element {
  const [open, setOpen] = useState<string | null>(null);
  /**
   * `round CN-B`, `CN-B.0(d)`, `WF-CON-31`: **« Réaffecter à <cabinet> », the
   * whole stop.** The target is the day's other cabinet with room for every
   * vehicle of the stop; where none has, the control keeps the drawing's look
   * greyed with the record's own sentence , « Capacité insuffisante : il
   * reste N places chez <cabinet> ce jeudi » , and names the fullest-fitting
   * cabinet. The room is read from the day detail the screen already holds;
   * the door reads it again under a lock.
   */
  const targetsFor = (
    vehicles: number,
  ): readonly { cabinet: CabinetDayView; remaining: number; fits: boolean }[] =>
    siblings
      .filter((one) => one.cabinetId !== cabinet.cabinetId)
      .map((one) => ({
        cabinet: one,
        remaining: one.capacity - one.placed,
        fits: one.capacity - one.placed >= vehicles,
      }))
      // The fullest-fitting first, then the rest: the order the single control used to choose by, kept as
      // the order they are OFFERED in.
      .sort((a, b) => b.remaining - a.remaining);
  // `C5.0.0(b)`: the link is a credential , `CopyableLink` holds the rule
  // (hidden by default, copied without being shown, hidden again on its own),
  // shared with the pièces card since `round C7`.
  const [showSheet, setShowSheet] = useState(false);
  const vehicles = cabinet.stops.reduce((sum, stop) => sum + stop.vehicles.length, 0);
  const sheet = cabinet.sheet;
  const diverged = sheet !== null && (sheet.diff.added.length > 0 || sheet.diff.removed.length > 0);
  const link = props.links[`${day.id}·${cabinet.cabinetId}`];
  return (
    <div data-testid={`cabinet-${cabinet.cabinetId}`} className="rounded-xl bg-gray-50 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-700">
          {cabinet.cabinetName} ·{" "}
          {fill("console.expertises.surCapacite", {
            placed: String(cabinet.placed),
            capacity: String(cabinet.capacity),
          })}
        </p>
        <p className="text-xs text-gray-500">
          {count(vehicles, "console.expertises.vehiculeUn", "console.expertises.vehicules")} ·{" "}
          {count(cabinet.stops.length, "console.expertises.arretUn", "console.expertises.arrets")}
        </p>
      </div>
      {/* `round C5-2`, `M4`, `OD-30`: the cabinet's own declaration, the signal
          below the contractual minimum, the overflow in rose with the
          reassignment as the gesture. Nothing when the minimum stands. */}
      {cabinet.declared === undefined || cabinet.declared === null ? null : (
        <p className="mt-1 text-xs text-gray-500" data-testid={`cabinet-${cabinet.cabinetId}-declaration`}>
          {fill("console.expertises.capaciteDeclaree", {
            declared: String(cabinet.declared),
            minimum: String(cabinet.minimum ?? DASH),
          })}
        </p>
      )}
      {cabinet.belowMinimum === true ? (
        <p
          className="mt-1 text-xs font-medium text-amber-700"
          data-testid={`cabinet-${cabinet.cabinetId}-sous-minimum`}
        >
          {fill("console.expertises.sousMinimum", { cabinet: cabinet.cabinetName })}
        </p>
      ) : null}
      {(cabinet.overflow ?? 0) > 0 ? (
        <p
          className="mt-1 text-xs font-medium text-rose-600"
          data-testid={`cabinet-${cabinet.cabinetId}-depassement`}
        >
          {fill("console.expertises.depassement", {
            declared: String(cabinet.capacity),
            confirmed: String(cabinet.placed),
            over: String(cabinet.overflow ?? 0),
          })}
        </p>
      ) : null}

      {cabinet.stops.length === 0 ? (
        <p className="mt-2 text-xs text-gray-500">{t("console.expertises.aucunArret")}</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {cabinet.stops.map((stop) => (
            <li
              key={stop.sellerOrganisationId}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-medium text-gray-700">
                  {stop.sellerName} ·{" "}
                  {count(
                    stop.vehicles.length,
                    "console.expertises.vehiculeUn",
                    "console.expertises.vehicules",
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(open === stop.sellerOrganisationId ? null : stop.sellerOrganisationId);
                    }}
                    className="text-xs font-medium text-indigo-600"
                    data-testid={`tournee-arret-${stop.sellerOrganisationId}`}
                  >
                    {open === stop.sellerOrganisationId
                      ? t("console.expertises.masquerDetail")
                      : t("console.expertises.voirQuiVaOu")}
                  </button>
                  {((): React.JSX.Element => {
                    /**
                     * `FX-3`, `M5 (d)` (`S3-3`): **one control per OTHER cabinet on the day, not one for the
                     * roomiest of them.**
                     *
                     * The screen offered a single « Réaffecter à <cabinet> », computed as the sibling with
                     * the most room. With three cabinets standing that hides two of the three choices
                     * `WF-CON-31` gives the chargée , *« `Réaffecter à <cabinet>` on a stop »* , and when the
                     * roomiest one is full the whole gesture reads as impossible while another cabinet has
                     * space. Each other cabinet now has its own control, and a full one is DISABLED with the
                     * record's own tooltip , « Capacité insuffisante : il reste N places chez <cabinet> ce
                     * jeudi » , which is the acceptance criterion in its own words: *« A reassignment that
                     * would exceed the receiving cabinet's Thursday capacity is impossible and says why. »*
                     */
                    const targets = targetsFor(stop.vehicles.length);
                    if (targets.length === 0) {
                      return (
                        <GuardedButton
                          disabled
                          reason={t("console.expertises.reaffecterSeul")}
                          variant="secondary"
                        >
                          {t("console.arrivees.reaffecter")}
                        </GuardedButton>
                      );
                    }
                    return (
                      <>
                        {targets.map((target) => {
                          const label = fill("console.expertises.reaffecterA", {
                            cabinet: target.cabinet.cabinetName,
                          });
                          return target.fits ? (
                            <button
                              key={target.cabinet.cabinetId}
                              type="button"
                              data-testid={`passage-reaffecter-${target.cabinet.cabinetId}`}
                              disabled={props.busy}
                              onClick={() => {
                                void props.onReassignStop(
                                  day.id,
                                  cabinet.cabinetId,
                                  stop.sellerOrganisationId,
                                  target.cabinet.cabinetId,
                                );
                              }}
                              className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                            >
                              {label}
                            </button>
                          ) : (
                            <GuardedButton
                              key={target.cabinet.cabinetId}
                              disabled
                              reason={fill("console.expertises.capaciteInsuffisante", {
                                places: String(Math.max(0, target.remaining)),
                                cabinet: target.cabinet.cabinetName,
                              })}
                              variant="secondary"
                              testId={`passage-reaffecter-${target.cabinet.cabinetId}`}
                            >
                              {label}
                            </GuardedButton>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              </div>
              {open === stop.sellerOrganisationId ? (
                <ul className="mt-2 space-y-1">
                  {stop.vehicles.map((vehicle) => (
                    <li key={vehicle.demandId} className="text-xs text-gray-600">
                      {vehicle.reference} · {vehicle.vehicleLabel} · {vehicle.vehiclePlate ?? DASH} ·{" "}
                      {vehicle.isStockControl
                        ? t("console.expertises.controleDeStock")
                        : (vehicle.clientName ?? DASH)}
                      {vehicle.scheduledAfterArrest ? (
                        <span className="ml-1 text-amber-700"> · {t("console.expertises.apresArret")}</span>
                      ) : null}
                      {/* `T-2b`, `M1 (d)`: the tour reads the dealer's mise à disposition, a fact of his door. */}
                      <span
                        className={`ml-1 ${vehicle.madeAvailableAt === null || vehicle.madeAvailableAt === undefined ? "text-gray-500" : "text-emerald-700"}`}
                        data-testid={`tournee-disposition-${vehicle.reference}`}
                      >
                        {" "}
                        ·{" "}
                        {vehicle.madeAvailableAt === null || vehicle.madeAvailableAt === undefined
                          ? t("console.expertises.dispositionAttendue")
                          : t("console.expertises.dispositionConfirmee")}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/* `WF-CON-32`: what leaves the building. */}
      <div className="mt-3 border-t border-gray-200 pt-3">
        {sheet === null ? (
          cabinet.placed === 0 ? (
            <GuardedButton disabled reason={t("console.expertises.rienNePart")} variant="primary">
              {t("console.expertises.envoyerFeuille")}
            </GuardedButton>
          ) : (
            <button
              type="button"
              data-testid="feuille-envoyer"
              disabled={props.busy}
              onClick={() => {
                props.onSendSheet(day.id, cabinet.cabinetId);
              }}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
            >
              {t("console.expertises.envoyerFeuille")}
            </button>
          )
        ) : diverged ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-amber-700">
              {fill("console.expertises.feuillePartie", { version: String(sheet.version) })}
            </span>
            <button
              type="button"
              data-testid="feuille-renvoyer"
              disabled={props.busy}
              onClick={() => {
                props.onSendSheet(day.id, cabinet.cabinetId);
              }}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
            >
              {fill("console.expertises.renvoyerFeuille", { version: String(sheet.version + 1) })}
            </button>
          </div>
        ) : sheet.acknowledgedVersion !== null && sheet.acknowledgedVersion < sheet.version ? (
          <span className="text-xs text-amber-700">
            {fill("console.expertises.accuseAncien", {
              acknowledged: String(sheet.acknowledgedVersion),
              version: String(sheet.version),
            })}
          </span>
        ) : sheet.acknowledgedAt !== null ? (
          <span data-testid="feuille-confirmee" className="text-xs text-emerald-700">
            {fill("console.expertises.feuilleConfirmee", {
              version: String(sheet.version),
              date: formatDate(new Date(sheet.acknowledgedAt)),
            })}
          </span>
        ) : (
          <span data-testid="feuille-attente" className="text-xs text-gray-600">
            {fill("console.expertises.feuilleEnvoyee", {
              version: String(sheet.version),
              cabinet: cabinet.cabinetName,
            })}
          </span>
        )}

        {sheet === null ? null : (
          <p className="mt-2 text-xs leading-relaxed text-gray-500">
            {fill("console.expertises.feuilleGarantie", {
              version: String(sheet.version),
              channel: sheet.sentChannel,
              date: formatDateTime(new Date(sheet.sentAt)),
              count: count(
                sheet.demandReferences.length,
                "console.expertises.vehiculeUn",
                "console.expertises.vehicules",
              ),
            })}
          </p>
        )}
        {sheet !== null && diverged ? (
          <p className="mt-1 text-xs leading-relaxed text-amber-700">
            {fill("console.expertises.feuillePortait", {
              version: String(sheet.version),
              sent: sheet.demandReferences.join(", "),
              live: String(cabinet.liveReferences.length),
              label: props.arrest.label,
            })}
          </p>
        ) : null}

        {/* `C5.1.1`: the sheet as the cabinet reads it , the VERSION's facts,
            never the live composition, so the two sides never disagree about
            what left. The drawing's folded block. */}
        {sheet === null ? null : (
          <button
            type="button"
            data-testid="feuille-voir"
            onClick={() => {
              setShowSheet(!showSheet);
            }}
            className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700"
          >
            {showSheet ? t("console.expertises.masquerFeuille") : t("console.expertises.voirFeuille")}
          </button>
        )}
        {sheet !== null && showSheet ? (
          <SheetFacts sheet={sheet} cabinetName={cabinet.cabinetName} dayLabel={day.label} />
        ) : null}

        {/* The link, once: a credential the office sends by hand. */}
        {link === undefined ? null : (
          <CopyableLink
            link={link}
            testId="feuille-lien"
            title={t("console.expertises.lienTitre")}
            text={t("console.expertises.lienTexte")}
          />
        )}
      </div>
    </div>
  );
}

/**
 * `round C5.1`, `C5.1.1`: **the version's facts, as the drawing folds them
 * under « Voir la feuille de tournée ».** Every fact is the version's own
 * (`SheetView.stops`); a version sent before `C5.1` carries none and the block
 * says so in the drawing's manner , an empty list carries its own sentence.
 * The fee is a dash on the header and on every line: the founder's grid is
 * not yet given, and a figure derived from a rule nobody recorded is a lie.
 */
function SheetFacts(props: {
  readonly sheet: SheetView;
  readonly cabinetName: string;
  readonly dayLabel: string;
}): React.JSX.Element {
  const { sheet } = props;
  const vehicles = sheet.stops.reduce((sum, stop) => sum + stop.vehicles.length, 0);
  return (
    <div
      data-testid="feuille-faits"
      className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3"
    >
      <div className="mb-2">
        <p className="text-xs font-medium text-gray-700">
          {fill("console.expertises.feuilleFaitsTitre", {
            version: String(sheet.version),
            cabinet: props.cabinetName,
            day: props.dayLabel,
          })}
        </p>
        <p className="mt-0.5 text-sm text-gray-500">
          {fill("console.expertises.feuilleFaitsTotal", {
            vehicles: count(vehicles, "console.expertises.vehiculeUn", "console.expertises.vehicules"),
            stops: count(sheet.stops.length, "console.expertises.arretUn", "console.expertises.arrets"),
            fee: DASH,
          })}
        </p>
      </div>
      {sheet.stops.length === 0 ? (
        <p className="text-sm text-gray-500">{t("console.expertises.feuilleSansFaits")}</p>
      ) : (
        <ol className="space-y-2.5">
          {sheet.stops.map((stop, rank) => (
            <li
              key={stop.sellerOrganisationId}
              data-testid={`feuille-faits-arret-${String(rank + 1)}`}
              className="flex items-start gap-2.5"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-700">
                {rank + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs leading-snug">
                  <span className="font-medium text-gray-700">{stop.sellerName}</span>
                  <span className="text-gray-500">
                    {" "}
                    · {stop.address ?? DASH} ·{" "}
                    {count(
                      stop.vehicles.length,
                      "console.expertises.vehiculeUn",
                      "console.expertises.vehicules",
                    )}
                  </span>
                </p>
                <p className="text-xs leading-snug text-gray-500">
                  {t("console.expertises.surPlace")}{" "}
                  {stop.contactName === null && stop.contactPhone === null
                    ? t("console.expertises.contactAPreciser")
                    : `${stop.contactName ?? DASH} · ${stop.contactPhone === null ? DASH : formatPhone(stop.contactPhone)}`}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {stop.vehicles.map((vehicle) => (
                    <li
                      key={vehicle.demandId}
                      data-testid={`feuille-faits-vehicule-${vehicle.reference}`}
                      className="text-xs leading-snug"
                    >
                      <span className="font-medium text-gray-700 tabular-nums">{vehicle.reference}</span>
                      <span className="text-gray-700">
                        {" "}
                        · {vehicle.vehicleLabel} ·{" "}
                        {vehicle.vehiclePlate === null ? DASH : formatPlate(vehicle.vehiclePlate)}
                      </span>
                      <span className="text-gray-500">
                        {" "}
                        · {fill("console.expertises.km", { n: formatQuantity(vehicle.mileageKm) })} ·{" "}
                        {vehicle.colour} · {DASH} {t("common.monnaie.fcfa")}
                      </span>
                      <span className="text-gray-500">
                        {" "}
                        · {vehicle.clientName ?? t("console.expertises.sansDemandeControleStock")}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          ))}
        </ol>
      )}
      <p className="mt-2 text-sm leading-relaxed text-gray-500">{t("console.expertises.feuilleFaitsNote")}</p>
    </div>
  );
}

// -------------------------------------------------------------------- queue

function Queue(props: ExpertisesProps): React.JSX.Element {
  return (
    <section data-testid="expertises-file" className="mb-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold">{t("console.expertises.fileTitre")}</h2>
        <span className="text-sm text-gray-500">
          {props.queue.length === 0
            ? t("console.expertises.fileAucune")
            : count(props.queue.length, "console.expertises.aConfirmerUne", "console.expertises.aConfirmer")}
        </span>
      </div>
      <ul className="space-y-3">
        {props.queue.map((line) => (
          <QueueCard key={line.demandId} line={line} {...props} />
        ))}
      </ul>
    </section>
  );
}

function QueueCard({ line, ...props }: ExpertisesProps & { readonly line: QueueLine }): React.JSX.Element {
  const [cabinetId, setCabinetId] = useState<string>(line.proposal.cabinetId ?? "");
  const [dayId, setDayId] = useState<string>(line.proposal.expertiseDayId ?? "");
  const changed =
    cabinetId !== (line.proposal.cabinetId ?? "") || dayId !== (line.proposal.expertiseDayId ?? "");
  const cabinets = props.days[0]?.cabinets ?? [];
  const day = props.days.find((one) => one.id === dayId);
  const full = (dId: string, cId: string): boolean => {
    const load = props.days.find((one) => one.id === dId)?.cabinets.find((one) => one.cabinetId === cId);
    return load !== undefined && load.placed >= load.capacity;
  };
  const arrested = day?.arrested === true;
  const canConfirm = cabinetId !== "" && dayId !== "" && !full(dayId, cabinetId);
  return (
    <li
      data-testid={`demande-${line.reference}`}
      className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-gray-900">
            {line.reference} · {line.vehicleLabel}
          </p>
          <p className="text-xs text-gray-500">
            {line.sellerName} · {line.clientName ?? t("console.expertises.controleDeStock")} ·{" "}
            {line.feesPaidOn === null ? DASH : formatDate(new Date(line.feesPaidOn))}
          </p>
        </div>
        <span
          data-testid="demande-chip"
          className={
            "rounded-full px-2 py-0.5 text-xs font-medium " +
            (changed ? "bg-amber-50 text-amber-700" : "bg-indigo-50 text-indigo-700")
          }
        >
          {changed ? t("console.expertises.choixEquipe") : t("console.expertises.propositionHakili")}
        </span>
      </div>
      {line.preventedReason === undefined || line.preventedReason === null ? null : (
        <p
          className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700"
          data-testid="demande-empeche"
        >
          {fill("console.expertises.controleEmpeche", {
            reason: preventedWords(line.preventedReason, line.preventedDetail ?? null),
          })}
        </p>
      )}
      {cabinets.length === 0 ? (
        <p className="mt-2 text-xs text-rose-700">{t("console.expertises.aucunCabinet")}</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            aria-label="cabinet"
            value={cabinetId}
            onChange={(event) => {
              setCabinetId(event.target.value);
            }}
            className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-indigo-400"
          >
            {cabinets.map((one) => (
              <option key={one.cabinetId} value={one.cabinetId} disabled={full(dayId, one.cabinetId)}>
                {one.cabinetName}
                {full(dayId, one.cabinetId) ? ` · ${t("console.expertises.cabinetComplet")}` : ""}
              </option>
            ))}
          </select>
          <select
            aria-label="jeudi"
            value={dayId}
            onChange={(event) => {
              setDayId(event.target.value);
            }}
            className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-indigo-400"
          >
            {props.days.map((one) => (
              <option key={one.id} value={one.id} disabled={full(one.id, cabinetId)}>
                {one.label}
                {full(one.id, cabinetId)
                  ? ` · ${t("console.expertises.complet")}`
                  : one.arrested
                    ? ` · ${t("console.expertises.jourArrete")}`
                    : ""}
              </option>
            ))}
          </select>
          <span className="text-xs text-gray-500">{t("console.expertises.vousDecidez")}</span>
        </div>
      )}
      <p className="mt-2 text-xs leading-relaxed text-gray-500">
        {changed ? "" : `${t("console.expertises.recommandation")} `}
        {line.proposal.motive}
      </p>
      <div className="mt-3">
        <button
          type="button"
          data-testid="demande-confirmer"
          disabled={props.busy || !canConfirm}
          onClick={() => {
            props.onSchedule(line.demandId, cabinetId, dayId, arrested);
          }}
          className={
            "rounded-lg px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40 " +
            (arrested ? "bg-amber-600 hover:bg-amber-700" : "bg-indigo-600 hover:bg-indigo-700")
          }
        >
          {arrested ? t("console.expertises.confirmerApresArret") : t("console.expertises.confirmer")}
        </button>
      </div>
    </li>
  );
}

// ------------------------------------------------------------------ reports

function Reports(props: ExpertisesProps): React.JSX.Element {
  const [open, setOpen] = useState<string | null>(null);
  // `C5.0.0(d)`: a report already filed leaves this list. The stored status
  // stays `programmee` after the report (05, `DECISION ADDED 05-08`: three
  // stored values, the verdict a view), so the filter reads the DISPLAY
  // status: a demand whose display status is its verdict has its report, and
  // the door refuses a second one (`RAPPORT_DEJA_DEPOSE`) , the section is
  // not a correction list, WF-CON-34's « Corriger le rapport » is unbuilt.
  const programmed = props.demands.filter((one) => one.displayStatus === "programmee");
  return (
    <section data-testid="expertises-rapports" className="mb-6">
      <h2 className="mb-3 text-sm font-semibold">{t("console.expertises.rapportsTitre")}</h2>
      {programmed.length === 0 ? (
        <p className="text-xs text-gray-500">{t("console.expertises.rapportsAucun")}</p>
      ) : (
        <ul className="space-y-2">
          {programmed.map((demand) => (
            <li key={demand.id} className="rounded-2xl border border-gray-200 bg-white px-5 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-700">
                  {demand.reference} · {demand.vehicleLabel} · {demand.sellerName}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {(() => {
                      const key = STATUS_WORDS.get(demand.displayStatus);
                      return key === undefined ? demand.displayStatus : t(key);
                    })()}
                  </span>
                  {props.grid === null ? null : (
                    <button
                      type="button"
                      data-testid={`rapport-${demand.reference}`}
                      disabled={props.busy}
                      onClick={() => {
                        setOpen(open === demand.id ? null : demand.id);
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t("console.expertises.saisirRapport")}
                    </button>
                  )}
                </div>
              </div>
              {open === demand.id && props.grid !== null ? (
                <div className="mt-3">
                  <ReportForm
                    grid={props.grid}
                    busy={props.busy}
                    onFile={(entry) => {
                      props.onFileReport(demand.id, entry);
                      setOpen(null);
                    }}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ------------------------------------------------------------------- escrow

/** `STATUTS_AFFICHES`' wire words, keyed as DATA, to the drawing's labels. */
const STATUS_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["aprogrammer", "console.expertises.statutAprogrammer"],
  ["programmee", "console.expertises.statutProgrammee"],
  ["reussie", "console.expertises.statutReussie"],
  ["ecartprix", "console.expertises.statutEcartprix"],
  ["echouee", "console.expertises.statutEchouee"],
  ["annulee", "console.expertises.statutAnnulee"],
]);

const STATE_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["attente", "console.expertises.etatAttente"],
  ["recu", "console.expertises.etatRecu"],
  ["acquis", "console.expertises.etatAcquis"],
  ["rembourse", "console.expertises.etatRembourse"],
]);
const MEANS_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["orange", "console.expertises.moyenOrange"],
  ["moov", "console.expertises.moyenMoov"],
  ["virement", "console.expertises.moyenVirement"],
]);

function Escrow(props: ExpertisesProps): React.JSX.Element {
  const [open, setOpen] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [receivedOn, setReceivedOn] = useState("");
  return (
    <section data-testid="expertises-sequestre" className="mb-6">
      <h2 className="text-sm font-semibold">{t("console.expertises.sequestreTitre")}</h2>
      <p className="mt-0.5 mb-3 text-sm leading-relaxed text-gray-500">
        {t("console.expertises.sequestreSous")}
      </p>
      <p className="mb-3 text-sm text-gray-500">{t("console.expertises.sequestreDessin")}</p>
      {/* `PAY-P`: the period's total , the prices RECEIVED (reçu, acquis)
          against the cabinets' fees due on them; the margin as Hakili
          derives it, read by the administration and the responsable. */}
      {props.fees.length === 0 ? null : (
        <p data-testid="sequestre-total" className="mb-3 text-sm font-medium text-gray-700">
          {(() => {
            const received = props.fees.filter((one) => one.state === "recu" || one.state === "acquis");
            const price = received.reduce((sum, one) => sum + (one.totalAmount ?? 0), 0);
            const known = received.filter(
              (one) => one.cabinetFeeAmount !== undefined && one.cabinetFeeAmount !== null,
            );
            const due = known.reduce((sum, one) => sum + (one.cabinetFeeAmount ?? 0), 0);
            // Found by the pair: a margin is never summed over a dash , with a
            // received control whose fee is unknown, the margin is a dash (R81).
            if (known.length < received.length) {
              return fill(
                known.length > 1
                  ? "console.expertises.sequestreTotalPartiel"
                  : "console.expertises.sequestreTotalPartielUn",
                {
                  received: formatMoney(price),
                  n: String(received.length),
                  k: String(known.length),
                  due: formatMoney(due),
                },
              );
            }
            return fill(
              received.length > 1
                ? "console.expertises.sequestreTotal"
                : "console.expertises.sequestreTotalUn",
              {
                received: formatMoney(price),
                n: String(received.length),
                due: formatMoney(due),
                margin: formatMoney(price - due),
              },
            );
          })()}
        </p>
      )}
      {props.fees.length === 0 ? (
        <p className="text-xs text-gray-500">{t("console.expertises.sequestreAucun")}</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-2xl border border-gray-200 bg-white">
          {props.fees.map((fee) => {
            // Founder's ruling of 16 October: every means is paid off-platform and confirmed by staff.
            const pendingTransfer = fee.method !== null && fee.state === "attente";
            const stateKey = STATE_WORDS.get(fee.state);
            const meansKey = fee.method === null ? undefined : MEANS_WORDS.get(fee.method);
            return (
              <li key={fee.id} data-testid={`frais-${fee.dossierReference ?? fee.id}`} className="px-5 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm text-gray-700">
                      {fee.dossierReference ?? DASH} · {fee.clientName ?? DASH} · {fee.vehicleLabel ?? DASH}
                    </p>
                    {/* `CN-B.0(b)`: what the office reads, never the fee's key. While
                        pending, the means and « demandé le <date> »; once confirmed,
                        the bank's reference and « reçu le <date> ». */}
                    {/* `PAY-P`: the margin line , the whole price against the cabinet's fee due; a dash where the fee is not known. */}
                    <p data-testid="frais-marge" className="text-sm text-gray-500">
                      {fill("console.expertises.margeLigne", {
                        price: fee.totalAmount === undefined ? DASH : formatMoney(fee.totalAmount),
                        fee:
                          fee.cabinetFeeAmount === undefined || fee.cabinetFeeAmount === null
                            ? DASH
                            : formatMoney(fee.cabinetFeeAmount),
                        margin:
                          fee.totalAmount === undefined ||
                          fee.cabinetFeeAmount === undefined ||
                          fee.cabinetFeeAmount === null
                            ? DASH
                            : formatMoney(fee.totalAmount - fee.cabinetFeeAmount),
                      })}
                    </p>
                    <p data-testid="frais-ligne-2" className="text-sm text-gray-500">
                      {fee.bankReference !== null && fee.receivedOn !== null
                        ? `${fee.bankReference} · ${fill("console.expertises.recuLe", {
                            date: formatDate(new Date(`${fee.receivedOn}T00:00:00Z`)),
                          })}`
                        : `${meansKey === undefined ? DASH : t(meansKey)} · ${
                            fee.declaredAt !== undefined && fee.declaredAt !== null
                              ? // #493 addendum 2: the client said he paid , a signal; the receipt is still Hakili's to confirm.
                                fill("console.expertises.declarePayeLe", {
                                  date: formatDate(new Date(fee.declaredAt)),
                                }) +
                                (fee.clientReference === undefined || fee.clientReference === null
                                  ? ""
                                  : fill("console.expertises.declareRef", { ref: fee.clientReference }))
                              : fee.requestedAt === null
                                ? DASH
                                : fill("console.expertises.coordonneesDemandeesLe", {
                                    date: formatDate(new Date(fee.requestedAt)),
                                  })
                          }`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium tabular-nums">{formatMoney(fee.clientAmount)}</span>
                    <span
                      data-testid="frais-etat"
                      className={
                        "rounded-full px-2 py-0.5 text-xs font-medium " +
                        (fee.state === "recu" || fee.state === "acquis"
                          ? "bg-emerald-50 text-emerald-700"
                          : fee.state === "rembourse"
                            ? "bg-gray-100 text-gray-600"
                            : "bg-amber-50 text-amber-700")
                      }
                    >
                      {stateKey === undefined ? fee.state : t(stateKey)}
                    </span>
                    {pendingTransfer ? (
                      <button
                        type="button"
                        data-testid="frais-confirmer"
                        disabled={props.busy}
                        onClick={() => {
                          setOpen(open === fee.id ? null : fee.id);
                        }}
                        className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                      >
                        {t("console.expertises.confirmerVirement")}
                      </button>
                    ) : (
                      <GuardedButton
                        disabled
                        reason={t("console.expertises.virementMotif")}
                        variant="secondary"
                      >
                        {t("console.expertises.confirmerVirement")}
                      </GuardedButton>
                    )}
                  </div>
                </div>
                {open === fee.id ? (
                  <form
                    data-testid="frais-formulaire"
                    className="mt-3 flex flex-wrap items-end gap-2 rounded-xl bg-gray-50 px-4 py-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      // `CN-B.0(a)`: the door's answer is the form's end. Accepted, the
                      // form closes and the row stands alone, re-read by the container;
                      // refused, it stays, with the refusal where the gesture is.
                      void (async () => {
                        const accepted = await props.onConfirmTransfer(
                          fee.id,
                          Number(amount),
                          reference.trim(),
                          receivedOn,
                        );
                        if (accepted) {
                          setOpen(null);
                          setAmount("");
                          setReference("");
                          setReceivedOn("");
                        }
                      })();
                    }}
                  >
                    <label className="text-xs text-gray-600">
                      {t("console.expertises.virementMontant")}
                      <input
                        required
                        inputMode="numeric"
                        value={amount}
                        onChange={(event) => {
                          setAmount(event.target.value);
                        }}
                        className="mt-1 block rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
                      />
                    </label>
                    <label className="text-xs text-gray-600">
                      {t("console.expertises.virementReference")}
                      <input
                        required
                        value={reference}
                        onChange={(event) => {
                          setReference(event.target.value);
                        }}
                        className="mt-1 block rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
                      />
                    </label>
                    <label className="text-xs text-gray-600">
                      {t("console.expertises.virementDate")}
                      <input
                        required
                        type="date"
                        value={receivedOn}
                        onChange={(event) => {
                          setReceivedOn(event.target.value);
                        }}
                        className="mt-1 block rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
                      />
                    </label>
                    <button
                      type="submit"
                      data-testid="frais-enregistrer"
                      disabled={props.busy || amount === "" || reference.trim() === "" || receivedOn === ""}
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
                    >
                      {t("console.expertises.virementEnregistrer")}
                    </button>
                  </form>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {props.refusal === null ? null : (
        <p role="alert" data-testid="expertises-refus" className="mt-3 text-sm text-rose-600">
          {props.refusal}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- the wrapper

export interface ExpertisesDoors {
  readonly readQueue: () => Promise<{ queue: readonly QueueLine[]; arrest: ArrestView }>;
  readonly readDays: () => Promise<readonly DayView[]>;
  readonly readDay: (dayId: string) => Promise<readonly CabinetDayView[]>;
  readonly readDemands: () => Promise<readonly DemandView[]>;
  readonly readGrid: () => Promise<readonly GridFamily[]>;
  readonly readFees: () => Promise<readonly EscrowFeeView[]>;
  readonly schedule: (
    demandId: string,
    cabinetId: string,
    dayId: string,
    afterArrestOverride: boolean,
  ) => Promise<void>;
  readonly sendSheet: (dayId: string, cabinetId: string) => Promise<{ link: string }>;
  readonly fileReport: (demandId: string, entry: ReportEntry) => Promise<void>;
  readonly confirmTransfer: (
    feeId: string,
    amount: number,
    bankReference: string,
    receivedOn: string,
  ) => Promise<void>;
  /** `CN-B.0(d)`: `POST /expertise/days/{id}/cabinets/{cabinetId}/stops/{seller}/reassign`. */
  readonly reassignStop: (
    dayId: string,
    fromCabinetId: string,
    sellerOrganisationId: string,
    toCabinetId: string,
  ) => Promise<void>;
}

export interface ExpertisesScreenProps extends ExpertisesDoors {
  readonly vue: string;
  /**
   * `C5.0.0(a)`: the cabinet's app origin for the environment. The door hands
   * back a path (`/tournee/<token>`); the link the office pastes is ABSOLUTE
   * on this origin , never on the console's, never a bare path.
   */
  readonly expertOrigin: string;
}

/** A read of `live` AFTER an await, which the linter cannot see change. */
const isLive = (run: { readonly live: boolean }): boolean => run.live;

const failureWords = (failure: unknown): string =>
  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");

export function ExpertisesScreen(props: ExpertisesScreenProps): React.JSX.Element | null {
  const [state, setState] = useState<{
    readonly queue: readonly QueueLine[];
    readonly arrest: ArrestView;
    readonly days: readonly DayView[];
    readonly demands: readonly DemandView[];
    readonly grid: readonly GridFamily[] | null;
    readonly fees: readonly EscrowFeeView[];
  } | null>(null);
  const [dayDetails, setDayDetails] = useState<Record<string, readonly CabinetDayView[]>>({});
  const [links, setLinks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const { readQueue, readDays, readDemands, readGrid, readFees, readDay } = props;

  useEffect(() => {
    const run: { live: boolean } = { live: true };
    void (async () => {
      try {
        const [queued, days, demands, grid, fees] = await Promise.all([
          readQueue(),
          readDays(),
          readDemands(),
          readGrid().catch(() => null),
          // The séquestre is the confirming right's; a responsable without it
          // still reads the tour, and the list is empty rather than refused.
          readFees().catch(() => []),
        ]);
        if (!run.live) return;
        setState({ queue: queued.queue, arrest: queued.arrest, days, demands, grid, fees });
        // Re-read every day already opened, so a schedule or a sheet lands.
        const opened = Object.keys(dayDetails);
        if (opened.length > 0) {
          const refreshed: Record<string, readonly CabinetDayView[]> = {};
          for (const id of opened) refreshed[id] = await readDay(id);
          if (isLive(run)) setDayDetails(refreshed);
        }
      } catch (failure) {
        if (run.live) setRefusal(failureWords(failure));
      }
    })();
    return () => {
      run.live = false;
    };
    // `dayDetails` is deliberately not a dependency: the effect re-reads the
    // days it finds open at the time it runs, and runs on `epoch`.
  }, [readQueue, readDays, readDemands, readGrid, readFees, readDay, epoch]);

  if (state === null) {
    return refusal === null ? null : (
      <p role="alert" data-testid="expertises-refus" className="text-sm text-rose-600">
        {refusal}
      </p>
    );
  }

  const act = (gesture: () => Promise<void>): Promise<boolean> =>
    (async () => {
      setBusy(true);
      setRefusal(null);
      try {
        await gesture();
        setEpoch((now) => now + 1);
        return true;
      } catch (failure) {
        setRefusal(failureWords(failure));
        return false;
      } finally {
        setBusy(false);
      }
    })();

  return (
    <Expertises
      vue={props.vue}
      queue={state.queue}
      arrest={state.arrest}
      days={state.days}
      dayDetails={dayDetails}
      demands={state.demands}
      grid={state.grid}
      fees={state.fees}
      links={links}
      busy={busy}
      refusal={refusal}
      onOpenDay={(dayId) => {
        if (dayId in dayDetails) {
          setDayDetails((now) => Object.fromEntries(Object.entries(now).filter(([id]) => id !== dayId)));
          return;
        }
        void (async () => {
          try {
            const detail = await readDay(dayId);
            setDayDetails((now) => ({ ...now, [dayId]: detail }));
          } catch (failure) {
            setRefusal(failureWords(failure));
          }
        })();
      }}
      onSchedule={(demandId, cabinetId, dayId, afterArrestOverride) => {
        void act(() => props.schedule(demandId, cabinetId, dayId, afterArrestOverride));
      }}
      onSendSheet={(dayId, cabinetId) => {
        void act(async () => {
          const { link } = await props.sendSheet(dayId, cabinetId);
          setLinks((now) => ({ ...now, [`${dayId}·${cabinetId}`]: `${props.expertOrigin}${link}` }));
        });
      }}
      onFileReport={(demandId, entry) => {
        void act(() => props.fileReport(demandId, entry));
      }}
      onConfirmTransfer={(feeId, amount, bankReference, receivedOn) =>
        act(() => props.confirmTransfer(feeId, amount, bankReference, receivedOn))
      }
      onReassignStop={(dayId, fromCabinetId, sellerOrganisationId, toCabinetId) =>
        act(() => props.reassignStop(dayId, fromCabinetId, sellerOrganisationId, toCabinetId))
      }
    />
  );
}
